import React from 'react';
import { PostApiMethod } from "../../core/services/apiService";
import { dashboardChartProps } from '../../core/dto/dashboard.dto';
import Chart from "react-apexcharts";

// Utility function to convert 24-hour time to 12-hour format
const convertTo12HourFormat = (time24: string): string => {
  // Handle time formats like "12:00", "00:00", "23:00"
  const [hours, minutes] = time24.split(':').map(Number);

  if (isNaN(hours)) return time24; // Return original if not a valid time

  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight, and 13-23 to 1-11

  return `${hours12.toString().padStart(2, '0')}:${minutes?.toString().padStart(2, '0') || '00'}${period}`;
};

const HeatMapChart = ({ apiEndpoint, startDate, endDate, selectedLocation, selectedAgent, selectedCallType, heatMapType, selectedCallDirection, onChartLoaded }: dashboardChartProps) => {
  const [heatMapChartDetail, setHeatMapChartDetail] = React.useState<any>();
  const [loaded, setLoaded] = React.useState(false);


  React.useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const formatDate = (date: Date | null) =>
          date ? date.toLocaleDateString("en-CA") : null;

        let inputStartDt = startDate ? new Date(startDate) : null;
        let inputEndDt = endDate ? new Date(endDate) : null;

        if (inputStartDt && inputEndDt) {
          const startStr = formatDate(inputStartDt);
          const endStr = formatDate(inputEndDt);

          if (startStr === endStr) {
            // If both dates are same, adjust start date to one week earlier
            const oneWeekBefore = new Date(inputEndDt);
            oneWeekBefore.setDate(inputEndDt.getDate() - 7);
            inputStartDt = oneWeekBefore;
          }
        }


        const payload: any = {
          from_date: formatDate(inputStartDt),
          to_date: formatDate(inputEndDt),
          heatMapType: heatMapType,
          location_id: selectedLocation,
          clientTZ: Intl.DateTimeFormat().resolvedOptions().timeZone
        };

        if (heatMapType === "forwardedCalls") {
          // For forwardedCalls, only include startDate, endDate, and selectedLocation
        } else {
          // For other heatMapTypes, include all parameters
          payload.call_type = selectedCallType;
          payload.agent_id = selectedAgent;

          if (heatMapType === "completedCalls") {
            payload['call_direction'] = selectedCallDirection ? selectedCallDirection : "";
          }
        }
        // if (heatMapType)

        const response = await PostApiMethod(apiEndpoint, payload);
        const rawData = response?.data?.data;

        // Convert time format from 24-hour to 12-hour in the series data
        const formattedData = rawData.map((series: any) => ({
          ...series,
          name: convertTo12HourFormat(series.name)
        }));

        const totalCount = rawData.reduce((sum: any, row: any) => {
          return sum + row.data.reduce((acc: any, curr: any) => acc + curr.y, 0);
        }, 0);

        const options = {
          chart: { type: 'heatmap', height: 350 },
          plotOptions: {
            heatmap: {
              shadeIntensity: 0.5,
              useFillColorAsStroke: true,
              colorScale: {
                ranges: [
                  { from: 0, to: 4, color: '#f5ffcb', name: 'Low' },
                  { from: 5, to: 10, color: '#4bc9c0', name: 'Medium' },
                  { from: 11, to: 50, color: '#0098c4', name: 'High' }
                ],
              },
            },
          },
          dataLabels: {
            enabled: true,
            dropShadow: { enabled: false },
            style: { colors: ['#30360a'], fontSize: '14px' }
          },
          xaxis: {
            type: 'category',
            title: {
              text: 'Day/Week of Month',
              style: { fontSize: '22px', fontWeight: 600 }
            },
            labels: {
              style: { fontSize: "14px" }
            }
          },
          yaxis: {
            title: {
              text: 'Hours',
              style: { fontSize: '22px', fontWeight: 600 }
            },
            labels: { style: { fontSize: '15px' } }
          },
          title: { text: 'Heatmap: Hours (X) vs Dates (Y)', align: 'center' }
        };

        let chartData = { labels: options, series: formattedData, count: totalCount }
        if (isMounted) {
          setHeatMapChartDetail(chartData);
          if (!loaded) {
            onChartLoaded?.();
            setLoaded(true);
          }
        }
      } catch (err) {
        console.error('Error fetching reports:', err);
      }
    })();
    return () => { isMounted = false };
  }, [apiEndpoint, startDate, endDate, selectedAgent, selectedLocation, heatMapType, selectedCallDirection, selectedCallType]);


  const heatMapChartData = React.useMemo(() =>
    heatMapChartDetail,
    [heatMapChartDetail]);

  return (
    <div className="overflow-hidden">
      <div className="row mb-3 chart-header" style={{ borderBottom: '2px solid #ddd', paddingBottom: '10px' }}>
        <div className="col-12">
          <h5 className="text-center"> {heatMapType === "missedCalls" ? "Missed Calls Heat Map" : heatMapType === "completedCalls" ? "Completed Calls Heat Map" : "Forwarded calls Heat Map"}</h5>
        </div>
      </div>
      <div className="col-12">
        {heatMapChartData && heatMapChartData.count > 0 ? (
          <Chart
            options={heatMapChartData.labels}
            series={heatMapChartData.series}
            type="heatmap" height={700} />
        ) : (
          <div className="text-center py-5">No Records Found</div>
        )}
      </div>
    </div>
  );
};

export default HeatMapChart;
